import { Home, Info } from 'lucide-react';
import ReportSection from './report/ReportSection';
import { tx } from '../i18n';
import { formatDate } from '../lib/format';
import { builtFormLabel, floorLevelLabel, propertyTypeLabel } from '../lib/report';
import type { HomeInterior } from '../types/api';

interface HomeInteriorSectionProps {
  interior: HomeInterior;
  /** When the report read its sources, for the line at the foot of the section. */
  checkedAt: string;
}

/**
 * The home itself, as the energy assessor surveyed it.
 *
 * <p>This is not a listing description and must not read like one: it is the only address-level
 * account of a dwelling in UK open data, and it counts rooms for a heat calculation rather than
 * for a tenant. The notes from the API carry that caveat, so they are shown, not summarised away.
 */
export default function HomeInteriorSection({ interior, checkedAt }: HomeInteriorSectionProps) {
  const rows: Array<{ term: string; value: string }> = [];
  const push = (term: string, value: string | null | undefined) => {
    if (value) {
      rows.push({ term, value });
    }
  };

  const type = interior.propertyType
    ? interior.builtForm && !interior.propertyType.toLowerCase().includes('flat')
      ? `${propertyTypeLabel(interior.propertyType)} · ${builtFormLabel(interior.builtForm)}`
      : propertyTypeLabel(interior.propertyType)
    : null;
  push(tx('Property type', 'Konut tipi',
    'Rodzaj lokalu',
    'Tipo de imóvel',
    'Tipo de vivienda'), type);
  push(tx('Floor area', 'Alan',
    'Powierzchnia',
    'Área',
    'Superficie'), interior.floorAreaSqm ? `${Math.round(interior.floorAreaSqm)} m²` : null);
  push(
    tx('Habitable rooms', 'Yaşam odası',
    'Pokoje mieszkalne',
    'Divisões habitáveis',
    'Estancias habitables'),
    interior.habitableRooms
      ? interior.heatedRooms && interior.heatedRooms !== interior.habitableRooms
        ? tx(
            `${interior.habitableRooms} (${interior.heatedRooms} heated)`,
            `${interior.habitableRooms} (${interior.heatedRooms} tanesi ısıtılıyor)`,
    `${interior.habitableRooms} (${interior.heatedRooms} ogrzewanych)`,
    `${interior.habitableRooms} (${interior.heatedRooms} com aquecimento)`,
    `${interior.habitableRooms} (${interior.heatedRooms} con calefacción)`,
          )
        : String(interior.habitableRooms)
      : null,
  );
  push(
    tx('Floor', 'Kat',
    'Piętro',
    'Piso',
    'Planta'),
    interior.floorLevel
      ? interior.topStorey
        ? `${floorLevelLabel(interior.floorLevel)} · ${tx('top storey', 'en üst kat',
    'najwyższe piętro',
    'último piso',
    'última planta')}`
        : floorLevelLabel(interior.floorLevel)
      : null,
  );
  push(
    tx('Storeys in the home', 'Konuttaki kat sayısı',
    'Kondygnacje w lokalu',
    'Pisos dentro da casa',
    'Plantas dentro de la vivienda'),
    interior.storeys && interior.storeys > 1 ? String(interior.storeys) : null,
  );
  push(tx('Built', 'Yapım dönemi',
    'Rok budowy',
    'Construção',
    'Construcción'), interior.constructionAgeBand);
  push(
    tx('Extensions', 'Ek yapılar',
    'Rozbudowy',
    'Ampliações',
    'Ampliaciones'),
    interior.extensions ? String(interior.extensions) : null,
  );
  push(tx('Walls', 'Duvarlar',
    'Ściany',
    'Paredes',
    'Muros'), interior.walls);
  push(tx('Roof', 'Çatı',
    'Dach',
    'Telhado',
    'Cubierta'), interior.roof);
  push(tx('Floor construction', 'Zemin',
    'Podłoga',
    'Pavimento',
    'Suelo'), interior.floorConstruction);
  push(tx('Windows', 'Pencereler',
    'Okna',
    'Janelas',
    'Ventanas'), interior.glazing);
  push(tx('Lighting', 'Aydınlatma',
    'Oświetlenie',
    'Iluminação',
    'Iluminación'), interior.lighting);

  if (rows.length === 0) {
    return null;
  }

  return (
    <ReportSection
      id="section-interior"
      icon={Home}
      title={tx('The home itself', 'Konutun kendisi',
    'Samo mieszkanie',
    'A própria casa',
    'La vivienda en sí')}
      informational
      quality={interior.exactAddress ? 'verified' : 'estimated'}
      source={{
        name: tx(
          'Energy Performance Certificate register',
          'Enerji Performans Sertifikası kaydı',
          'Rejestr świadectw charakterystyki energetycznej',
          'Registo de certificados energéticos',
          'Registro de certificados energéticos',
        ),
        published: interior.recordedOn,
        checked: checkedAt,
      }}
      note={
        interior.recordedOn
          ? tx(
              `From the Energy Performance Certificate, assessed ${formatDate(interior.recordedOn)}.`,
              `Enerji Performans Sertifikasından, değerlendirme tarihi ${formatDate(interior.recordedOn)}.`,
    `Ze świadectwa charakterystyki energetycznej, ocena z ${formatDate(interior.recordedOn)}.`,
    `Do certificado energético, avaliado em ${formatDate(interior.recordedOn)}.`,
    `Del certificado energético, evaluado el ${formatDate(interior.recordedOn)}.`,
            )
          : tx(
              'From the Energy Performance Certificate.',
              'Enerji Performans Sertifikasından.',
    'Ze świadectwa charakterystyki energetycznej.',
    'Do certificado energético.',
    'Del certificado energético.',
            )
      }
    >
      <dl className="facts-list">
        {rows.map((row) => (
          <div key={row.term} className="facts-list__row">
            <dt>{row.term}</dt>
            <dd>{row.value}</dd>
          </div>
        ))}
      </dl>

      {interior.notes.length > 0 ? (
        <ul className="remarks">
          {interior.notes.map((note) => (
            <li key={note}>
              <Info size={13} aria-hidden="true" />
              <span>{note}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </ReportSection>
  );
}
